import React, { useEffect, useCallback, useState } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Input,
  Table,
  Alert,
  Spinner,
  Badge,
} from "reactstrap"
import { useDropzone } from "react-dropzone"
import "../../newcomponents/index.css"
import { useSelector, connect } from "react-redux"
import { setBreadcrumbItems } from "../../store/actions"
import axios from "axios"
import config from "config/config"
import CustomerDetails from "./CustomerDetails"
import SnackbarComponent from "components/SnackBar"
import UploadModal from "./UploadModal"
import { formatDate } from "helpers/date-format"
import MiniCards from "newcomponents/mini-cards"

const CustomerDashboard = props => {
  const activeOrganization = useSelector(
    state => state.organizationReducer.activeOrganization,
  )
  const userData = useSelector(state => state.Login.userData)
  const mode = useSelector(state => state.Layout.layoutMode)

  const [uploadedFile, setUploadedFile] = useState(null)
  const [visible, setVisible] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [err, setErr] = useState(null)
  const [success, setSuccess] = useState(null)
  const [loading, setLoading] = useState(false)
  const [customers, setCustomers] = useState([])
  const [repeatCustomers, setRepeatCustomers] = useState(0)
  const [mostAvailedService, setMostAvailedService] = useState("")
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [customerDetailsComponentOpened, setCustomerDetailsComponentOpened] =
    useState(false)

  // 🔹 Pagination States
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  const totalRecords = customers.length
  const totalPages = Math.ceil(totalRecords / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage

  // Pagination range function
  const getPaginationRange = () => {
    const delta = 2
    const range = []
    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i)
    }
    if (currentPage - delta > 2) range.unshift("...")
    if (currentPage + delta < totalPages - 1) range.push("...")
    range.unshift(1)
    if (totalPages > 1) range.push(totalPages)
    return range
  }

  const pages = getPaginationRange()

  const onDismiss = () => setVisible(false)

  const onDrop = useCallback(acceptedFiles => {
    if (acceptedFiles.length > 0) {
      setUploadedFile(acceptedFiles[0])
    }
  }, [])

  useEffect(() => {
    const breadcrumbItems = [
      { title: `Dashboard`, link: "/dashboard" },
      { title: "Customers", link: "#" },
    ]
    props.setBreadcrumbItems(
      `${activeOrganization.name} (${activeOrganization.type})`,
      breadcrumbItems,
    )
  }, [activeOrganization, props])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ".csv, .xlsx",
    multiple: false,
    name: "file",
  })

  const reports = [
    {
      title: "Customers",
      iconClass: "account-group-outline",
      total: `${customers.length}`,
      badgecolor: "info",
    },
    {
      title: "Repeat Visits",
      iconClass: "account-convert-outline",
      total: `${repeatCustomers}`,
      badgecolor: "info",
    },
    {
      title: "Most Availed Service",
      iconClass: "face-agent",
      total: `${mostAvailedService === "" ? "Nil" : mostAvailedService}`,
      badgecolor: "info",
    },
  ]
  console.log("mostAvailedService", mostAvailedService)
  const toggleModal = () => {
    setModalOpen(!modalOpen)
  }

  const fetchCustomers = async () => {
    try {
      setLoading(true)
      const response = await axios.get(
        `${config.backendUrl}/customers/${activeOrganization.id}`,
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
          },
        },
      )

      const repeatCustomers = response.data.customers.filter(
        customer => customer.CustomerVisits.length > 1,
      )
      setRepeatCustomers(repeatCustomers.length)

      const serviceCount = {}

      response.data.customers.forEach(customer => {
        customer.CustomerVisits.forEach(visit => {
          const service = visit.service
          if (serviceCount[service]) {
            serviceCount[service]++
          } else {
            serviceCount[service] = 1
          }
        })
      })

      let mostAvailedService = ""
      let maxCount = 0

      for (const service in serviceCount) {
        if (serviceCount[service] > maxCount) {
          mostAvailedService = service
          maxCount = serviceCount[service]
        }
      }

      setMostAvailedService(mostAvailedService)
      setCustomers(response.data.customers)
      setLoading(false)
    } catch (err) {
      setErr("Error Fetching Customers")
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCustomers()
  }, [customerDetailsComponentOpened, activeOrganization])

  const handleSearch = e => {
    setSearchTerm(e.target.value)
    setCurrentPage(1)
  }

  const handleUpload = async columnMapping => {
    if (!uploadedFile || Object.keys(columnMapping).length === 0) {
      console.error("No file uploaded or column mapping missing.")
      return
    }

    setLoading(true)
    const formData = new FormData()
    formData.append("file", uploadedFile)
    formData.append("columnMapping", JSON.stringify(columnMapping))

    try {
      const response = await axios.post(
        `${config.backendUrl}/customers/upload?orgId=${activeOrganization.id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      )

      if (response.status === 200) {
        setSuccess(response.data.message)
        setUploadedFile(null)
        setLoading(false)
        setCustomers([])
        fetchCustomers()
        setModalOpen(false)
      } else {
        setErr(
          "Error Uploading File, Please use only XLSX or CSV and follow required format.",
        )
        setUploadedFile(null)
        setLoading(false)
        setModalOpen(false)
      }
    } catch (error) {
      if (error.response?.status === 400) {
        setErr(error.response.data.message)
      }
      console.error("Error uploading file:", error)
      setLoading(false)
    }
  }

  // 🔹 Filter + Paginate data
  const filteredCustomers = customers.filter(customer =>
    `${customer.firstName} ${customer.lastName} ${customer.email}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase()),
  )
  const paginatedCustomers = filteredCustomers.slice(startIndex, endIndex)

  return (
    <>
      {customerDetailsComponentOpened ? (
        <CustomerDetails
          customer={selectedCustomer}
          setCustomerDetailsComponentOpened={setCustomerDetailsComponentOpened}
          fetch={fetchCustomers}
        />
      ) : (
        <React.Fragment>
          <SnackbarComponent
            err={err}
            success={success}
            setErr={setErr}
            setSuccess={setSuccess}
          />
          {/* <Miniwidgets reports={reports || []} loading={loading} noFilter /> */}
          {/* Cards Section */}
          <MiniCards reports={reports} mode={mode} />
          {/* 🔹 Customers Card */}
          <Row className="mb-0 mt-0">
            <Col xl={12}>
              {/* 🔹 Card Section */}
              <Card
                style={{
                  borderRadius: "20px",
                  marginBottom: "0",
                  marginTop: "0px",
                }}
              >
                <CardBody>
                  <Row className="align-items-center mb-2">
                    <Col xl={6} lg={6} md={6}>
                      <h4
                        style={{
                          color: mode === "dark" ? "#fff" : "#2990DF",
                        }}
                        className="text-uppercase mb-0"
                      >
                        Customers
                      </h4>
                    </Col>
                    <Col
                      xl={6}
                      lg={6}
                      md={6}
                      className="d-flex justify-content-end align-items-center"
                    >
                      <Input
                        type="text"
                        placeholder="Search Customers..."
                        value={searchTerm}
                        onChange={handleSearch}
                        style={{ width: "200px", marginRight: "10px" }}
                      />
                      {userData.role !== "viewer" && (
                        <Button
                          style={{
                            backgroundColor: "#2990DF",
                          }}
                          onClick={toggleModal}
                        >
                          <span className="mdi mdi-upload"></span>
                        </Button>
                      )}
                    </Col>
                  </Row>

                  {loading ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "30vh",
                      }}
                    >
                      <Spinner
                        style={{
                          color: mode === "dark" ? "#fff" : "#2990DF",
                          height: "3rem",
                          width: "3rem",
                        }}
                      >
                        Loading...
                      </Spinner>
                    </div>
                  ) : filteredCustomers && filteredCustomers.length > 0 ? (
                    <div
                      className="table-responsive"
                      style={{ maxHeight: "40vh" }}
                    >
                      <Table striped>
                        <thead className="sticky-header">
                          <tr>
                            <th>ID</th>
                            <th>First Name</th>
                            <th>Last Name</th>
                            <th>Email</th>
                            <th>Contact</th>
                            <th>Imported At</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedCustomers.map((customer, index) => (
                            <tr
                              key={customer.id}
                              style={{ cursor: "pointer" }}
                              onClick={() => {
                                setSelectedCustomer(customer)
                                setCustomerDetailsComponentOpened(true)
                              }}
                            >
                              <td>{startIndex + index + 1}</td>
                              <td>{customer.firstName}</td>
                              <td>{customer.lastName}</td>
                              <td>{customer.email}</td>
                              <td>{customer.contact}</td>
                              <td>{formatDate(customer.createdAt)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <div
                      className="text-center"
                      style={{
                        height: "30vh",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      No customers found, Please upload customers.
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
          {/* 🔹 Pagination  Card */}
          {filteredCustomers && filteredCustomers.length > 0 && (
            <Row>
              <Col xl={12}>
                <div
                  style={{
                    marginTop: "10px",
                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                    borderRadius: "20px",
                    backgroundColor: mode === "dark" ? "#252f40" : "#fff",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "20px",
                    color: mode === "dark" ? "#9ca3af" : "#6b7280",
                    fontSize: "14px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <span>Showing</span>
                    <select
                      value={itemsPerPage}
                      onChange={e => {
                        setItemsPerPage(Number(e.target.value))
                        setCurrentPage(1)
                      }}
                      style={{
                        padding: "6px 30px 6px 12px",
                        border: `1px solid ${
                          mode === "dark" ? "#3a4556" : "#d1d5db"
                        }`,
                        borderRadius: "6px",
                        background: mode === "dark" ? "#1a2332" : "#fff",
                        color: mode === "dark" ? "#fff" : "#000",
                        fontSize: "14px",
                        cursor: "pointer",
                        outline: "none",
                        appearance: "none",
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 8px center",
                      }}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span>of {filteredCustomers.length} records</span>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: "4px",
                      alignItems: "center",
                    }}
                  >
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      style={{
                        padding: "6px 10px",
                        border: "none",
                        background: "transparent",
                        color:
                          currentPage === 1
                            ? mode === "dark"
                              ? "#4b5563"
                              : "#d1d5db"
                            : mode === "dark"
                              ? "#9ca3af"
                              : "#6b7280",
                        cursor: currentPage === 1 ? "not-allowed" : "pointer",
                        fontSize: "18px",
                      }}
                    >
                      «
                    </button>
                    <button
                      onClick={() =>
                        setCurrentPage(prev => Math.max(prev - 1, 1))
                      }
                      disabled={currentPage === 1}
                      style={{
                        padding: "6px 10px",
                        border: "none",
                        background: "transparent",
                        color:
                          currentPage === 1
                            ? mode === "dark"
                              ? "#4b5563"
                              : "#d1d5db"
                            : mode === "dark"
                              ? "#9ca3af"
                              : "#6b7280",
                        cursor: currentPage === 1 ? "not-allowed" : "pointer",
                        fontSize: "18px",
                      }}
                    >
                      ‹
                    </button>

                    {pages.map((page, index) =>
                      page === "..." ? (
                        <span key={`ellipsis-${index}`}>...</span>
                      ) : (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          style={{
                            padding: "6px 12px",
                            border: "none",
                            borderRadius: "6px",
                            background:
                              currentPage === page ? "#2990DF" : "transparent",
                            color:
                              currentPage === page
                                ? "#fff"
                                : mode === "dark"
                                  ? "#9ca3af"
                                  : "#6b7280",
                            cursor: "pointer",
                            minWidth: "36px",
                            fontWeight: currentPage === page ? "600" : "400",
                          }}
                        >
                          {page}
                        </button>
                      ),
                    )}

                    <button
                      onClick={() =>
                        setCurrentPage(prev => Math.min(prev + 1, totalPages))
                      }
                      disabled={currentPage === totalPages}
                      style={{
                        padding: "6px 10px",
                        border: "none",
                        background: "transparent",
                        color:
                          currentPage === totalPages
                            ? mode === "dark"
                              ? "#4b5563"
                              : "#d1d5db"
                            : mode === "dark"
                              ? "#9ca3af"
                              : "#6b7280",
                        cursor:
                          currentPage === totalPages
                            ? "not-allowed"
                            : "pointer",
                        fontSize: "18px",
                      }}
                    >
                      ›
                    </button>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      style={{
                        padding: "6px 10px",
                        border: "none",
                        background: "transparent",
                        color:
                          currentPage === totalPages
                            ? mode === "dark"
                              ? "#4b5563"
                              : "#d1d5db"
                            : mode === "dark"
                              ? "#9ca3af"
                              : "#6b7280",
                        cursor:
                          currentPage === totalPages
                            ? "not-allowed"
                            : "pointer",
                        fontSize: "18px",
                      }}
                    >
                      »
                    </button>
                  </div>
                </div>
              </Col>
            </Row>
          )}

          <UploadModal
            modalOpen={modalOpen}
            mode={mode}
            toggleModal={toggleModal}
            uploadedFile={uploadedFile}
            setUploadedFile={setUploadedFile}
            handleUpload={columnMapping => handleUpload(columnMapping)}
            loading={loading}
          />
        </React.Fragment>
      )}
    </>
  )
}

export default connect(null, { setBreadcrumbItems })(CustomerDashboard)
